import React, { useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { Stack, Typography, Alert, CircularProgress, Box, FormControl, InputLabel, Select, MenuItem, useTheme } from '@mui/material';
import { CHART_VIEWS } from './ItemAnalytics';

// Group types enum
const GROUP_TYPES = {
    CARD: 'card',
    PROCEDURE: 'procedure',
    HOSPITAL: 'hospital',
    PHYSICIAN: 'physician'
};
const MAX_ITEMS = 8; // Max items to show before aggregating into "Others"

function CardCasePieChart({ 
    chartView, 
    groupingMaps, 
    isLoading, 
    error,
    groupBy = 'card',
    selectedActivity = 'picked',
    onGroupByChange,
    onActivityChange
}) {
    const theme = useTheme();
    const currentGrouping = groupBy;

    // Get available activities based on chart view
    const availableActivities = useMemo(() => {
        if (chartView === CHART_VIEWS.PICKLIST_VIEW) {
            return [
                { value: 'run', label: 'Run', color: theme.palette.analytics.cardTypes.run },
                { value: 'restock', label: 'Restock', color: theme.palette.analytics.cardTypes.restock },
                { value: 'trashed', label: 'Trashed', color: theme.palette.analytics.cardTypes.openNotUsed }
            ];
        } else if (chartView === CHART_VIEWS.INVENTORY_VIEW) {
            return [
                { value: 'picked', label: 'Picked', color: theme.palette.analytics.cardTypes.picked },
                { value: 'used', label: 'Used', color: theme.palette.analytics.cardTypes.used }
            ];
        } else {
            return [
                { value: 'used', label: 'Used', color: theme.palette.analytics.cardTypes.used }
            ];
        }
    }, [chartView, theme]);

    const { pieData, othersData } = useMemo(() => {
        if (!groupingMaps || !groupingMaps[currentGrouping]) {
            return { pieData: [], othersData: null };
        }
        
        // Get the activity key for the selected activity
        const activityKey = `${selectedActivity}_quantity`;
        
        // Transform to pie format and filter/sort
        const sortedGroupValues = Object.values(groupingMaps[currentGrouping] || {})
            .filter(group => (group[activityKey] || 0) > 0)
            .sort((a, b) => (b[activityKey] || 0) - (a[activityKey] || 0));

        let processedGroups = sortedGroupValues;
        let othersInfo = null;
        
        if (sortedGroupValues.length > MAX_ITEMS) {
            const individualItems = sortedGroupValues.slice(0, MAX_ITEMS);
            const othersItems = sortedGroupValues.slice(MAX_ITEMS);
            const othersTotal = othersItems.reduce((sum, item) => sum + (item[activityKey] || 0), 0);
            
            // Calculate others percentage
            const totalValue = sortedGroupValues.reduce((sum, item) => sum + (item[activityKey] || 0), 0);
            const othersPercentage = totalValue > 0 ? ((othersTotal / totalValue) * 100).toFixed(1) : '0.0';
            
            othersInfo = {
                count: othersItems.length,
                value: othersTotal,
                percentage: othersPercentage
            };

            processedGroups = individualItems;
        }

        const chartData = processedGroups.map((group, index) => {
            return {
                id: group.name,
                label: group.name,
                value: group[activityKey] || 0,
                index: index
            };
        });
        
        return { pieData: chartData, othersData: othersInfo };
    }, [chartView, groupingMaps, currentGrouping, selectedActivity, availableActivities]);

    const baseColor = useMemo(() => {
        // Get the base color for the selected activity
        const selectedActivityObj = availableActivities.find(a => a.value === selectedActivity);
        return selectedActivityObj?.color || theme.palette.analytics.cardTypes.picked;
    }, [availableActivities, selectedActivity, theme]);


    // Handle click on pie segments for navigation
    const handlePieClick = (data) => {
        if (!groupingMaps) return;
        
        if (currentGrouping === GROUP_TYPES.CARD) {
            // Navigate to individual card page
            const cardData = Object.values(groupingMaps.card || {})
                .find(card => card.name === data.id);
            
            if (cardData && cardData.id) {
                window.open(`${window.location.origin}/cards/${cardData.id}`, '_blank');
            }
        } else if (currentGrouping === GROUP_TYPES.PHYSICIAN) {
            // Navigate to CardList filtered by physician
            const physicianData = Object.values(groupingMaps.physician || {})
                .find(physician => physician.name === data.id);
            
            if (physicianData) {
                // Parse physician name (assuming format "Last, First" or "First Last")
                const physicianName = physicianData.name;
                let firstName = '';
                let lastName = '';
                
                if (physicianName.includes(', ')) {
                    // Format: "Last, First"
                    const parts = physicianName.split(', ');
                    lastName = parts[0];
                    firstName = parts[1] || '';
                } else {
                    // Format: "First Last" or just "Name"
                    const parts = physicianName.split(' ');
                    firstName = parts[0] || '';
                    lastName = parts.slice(1).join(' ') || '';
                }
                
                // Set the physician in localStorage for CardList to pick up
                localStorage.setItem('selectedPhysician', JSON.stringify({
                    name: physicianName,
                    first_name: firstName,
                    last_name: lastName
                }));
                
                // Navigate to cards list
                window.open(`${window.location.origin}/cards`, '_blank');
            }
        }
    };

    if (isLoading || !groupingMaps) {
        return (
            <Stack spacing={2} alignItems="center" py={4}>
                <CircularProgress />
                <Typography>Loading card case data...</Typography>
            </Stack>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Error loading card case data: {error.message || 'Please try again'}
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Card Case Distribution
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="group-type-label">Group By</InputLabel>
                    <Select
                        labelId="group-type-label"
                        value={currentGrouping}
                        label="Group By"
                        onChange={(event) => onGroupByChange?.(event.target.value)}
                    >
                        <MenuItem value={GROUP_TYPES.CARD}>Card</MenuItem>
                        <MenuItem value={GROUP_TYPES.PROCEDURE}>Procedure</MenuItem>
                        <MenuItem value={GROUP_TYPES.HOSPITAL}>Hospital</MenuItem>
                        <MenuItem value={GROUP_TYPES.PHYSICIAN}>Physician</MenuItem>
                    </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="activity-label">Activity</InputLabel>
                    <Select
                        labelId="activity-label"
                        value={selectedActivity}
                        label="Activity"
                        onChange={(event) => onActivityChange?.(event.target.value)}
                    >
                        {availableActivities.map((activity) => (
                            <MenuItem key={activity.value} value={activity.value}>
                                {activity.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {pieData.length === 0 && (
                <Alert severity="info">
                    No card case data available for the selected activity and date range.
                </Alert>
            )}
            {pieData.length > 0 && (
                <Stack spacing={2}>
                    <Box sx={{ height: 500 }}>
                        <ResponsivePie
                            key={`responsive-pie-${chartView}-${groupBy}-${selectedActivity}`}
                            data={pieData}
                            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                            innerRadius={0.2}
                            padAngle={0.7}
                            cornerRadius={5}
                            activeOuterRadiusOffset={8}
                            borderWidth={0.5}
                            borderColor={{
                                from: 'color',
                                modifiers: [['darker', 0.9]]
                            }}
                            colors={baseColor}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsTextColor="#333333"
                            arcLinkLabelsThickness={5}
                            arcLinkLabelsColor={{ from: 'color' }}
                            arcLinkLabel={(d) => d.label.length > 20 ? `${d.label.substring(0, 17)}...` : d.label}
                            arcLabelsSkipAngle={10}
                            arcLabelsTextColor="#ffffff"
                            theme={{
                                labels: {
                                    text: {
                                        fontSize: 16,
                                    }
                                }
                            }}
                            tooltip={({ datum }) => (
                                <Box sx={{
                                    background: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    boxShadow: theme.shadows[3],
                                    px: 1.5,
                                    py: 1,
                                }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'keep-word' }}>
                                        {datum.label}
                                    </Typography>
                                    <hr style={{ margin: '4px 0', borderColor: theme.palette.divider }} />
                                    <Typography variant="body2">{datum.value.toLocaleString()}</Typography>
                                </Box>
                            )}
                            onClick={handlePieClick}
                        />
                    </Box>
                    
                    {/* Chart Information */}
                    <Box sx={{ 
                        textAlign: 'center', 
                        p: 2, 
                        bgcolor: 'action.hover', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Showing Top {Math.min(MAX_ITEMS, pieData.length)} {currentGrouping.charAt(0).toUpperCase() + currentGrouping.slice(1)}s
                            {' by '}{availableActivities.find(a => a.value === selectedActivity)?.label}
                        </Typography>
                        {othersData && (
                            <>
                                <Typography variant="body1" sx={{ fontWeight: 500, mt: 1 }}>
                                    Others = {othersData.value.toLocaleString()} ({othersData.percentage}%)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({othersData.count} additional {currentGrouping}s not shown in chart)
                                </Typography>
                            </>
                        )}
                    </Box>
                </Stack>
            )}
        </Stack>
    );
}

export default CardCasePieChart;